import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  HandHeart, Copy, Plus, Trash2, Users, Send, ChevronDown, ChevronRight, Mail, Phone,
} from "lucide-react";

const DEFAULT_WAIVER = `I understand that volunteering carries inherent risks. I release the event organizer and AddVendTour from any claims arising from my participation. I confirm I am at least 18 years of age (or have written parental consent). I agree to follow all safety instructions from the event host on the day.`;

/**
 * VolunteerHostPanel — organizer's control surface for volunteer sign-ups.
 * Mounted on `/events/:id` for event owners. Two-tab UX: Configure (waiver +
 * slots + toggle) and Signups (roster of volunteers with reply-back).
 */
export default function VolunteerHostPanel({ eventId }) {
  const [cfg, setCfg] = useState(null);
  const [volunteers, setVolunteers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [replyDraft, setReplyDraft] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [cfgRes, listRes] = await Promise.all([
        api.get(`/events/${eventId}/volunteers/config`),
        api.get(`/events/${eventId}/volunteers`),
      ]);
      setCfg(cfgRes.data);
      setVolunteers(listRes.data.items || []);
    } catch {
      // If we can't load (usually because we're not the owner), silently hide the panel.
      setCfg(null);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (eventId) load(); /* eslint-disable-next-line */ }, [eventId]);

  const save = async (patch) => {
    setBusy(true);
    try {
      const next = { ...cfg, ...patch };
      await api.put(`/events/${eventId}/volunteers/config`, next);
      setCfg(next);
      toast.success("Volunteer settings saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  const addSlot = () => {
    const slots = [...(cfg.slots || []), {
      id: null, label: "New shift", starts_at: "", ends_at: "",
      max_volunteers: 2, description: "",
    }];
    save({ slots });
  };

  const updateSlot = (idx, key, value) => {
    const slots = [...(cfg.slots || [])];
    slots[idx] = { ...slots[idx], [key]: value };
    setCfg({ ...cfg, slots });
  };

  const removeSlot = (idx) => {
    const slots = [...(cfg.slots || [])];
    slots.splice(idx, 1);
    save({ slots });
  };

  const copyShareLink = async () => {
    const url = `${window.location.origin}/volunteer/${eventId}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Share link copied");
    } catch { toast.error("Copy failed"); }
  };

  const sendReply = async (vid) => {
    const text = (replyDraft[vid] || "").trim();
    if (!text) return toast.error("Type your reply first.");
    try {
      await api.post(`/events/${eventId}/volunteers/${vid}/reply`, { reply: text });
      toast.success("Reply sent");
      setReplyDraft((d) => ({ ...d, [vid]: "" }));
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Send failed");
    }
  };

  if (loading || !cfg) return null;

  return (
    <div
      data-testid="volunteer-host-panel"
      className="mt-8 rounded-xl p-6 border-2"
      style={{ borderColor: "var(--avt-lime)", backgroundColor: "rgba(132,204,22,0.05)" }}
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest"
               style={{ color: "#4B7A0A" }}>
            <HandHeart className="h-3 w-3" /> Volunteer sign-ups
          </div>
          <div className="font-serif text-xl mt-1">Recruit helpers with one shareable link</div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs" style={{ color: "var(--ink-soft)" }}>
            {cfg.enabled ? "Sign-up open" : "Sign-up closed"}
          </span>
          <Switch
            data-testid="volunteer-enable-toggle"
            checked={cfg.enabled}
            onCheckedChange={(v) => save({ enabled: v })}
          />
        </div>
      </div>

      {cfg.enabled && (
        <>
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <div className="flex-1 min-w-0 p-2 rounded-lg text-sm font-mono truncate"
                 style={{ backgroundColor: "var(--sand)", color: "var(--ink-soft)" }}
                 data-testid="volunteer-share-url">
              {`${window.location.origin}/volunteer/${eventId}`}
            </div>
            <Button
              data-testid="volunteer-copy-link"
              size="sm"
              onClick={copyShareLink}
              className="rounded-full"
            >
              <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy link
            </Button>
          </div>

          {/* Config dialog */}
          <div className="mt-5 flex items-center gap-3 flex-wrap">
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="outline" className="rounded-full" data-testid="volunteer-configure-btn">
                  Configure shifts &amp; waiver
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="volunteer-config-dialog">
                <DialogHeader>
                  <DialogTitle>Volunteer settings</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label className="text-xs uppercase tracking-widest">Instructions to volunteers</Label>
                    <Textarea rows={2} maxLength={1000}
                      data-testid="volunteer-instructions-input"
                      value={cfg.instructions || ""}
                      onChange={(e) => setCfg({ ...cfg, instructions: e.target.value })}
                      onBlur={() => save({ instructions: cfg.instructions })}
                      placeholder="What to bring, where to check in, dress code…" />
                  </div>
                  <div>
                    <div className="flex items-center justify-between">
                      <Label className="text-xs uppercase tracking-widest">Shifts</Label>
                      <Button size="sm" variant="ghost"
                              onClick={addSlot}
                              data-testid="volunteer-add-slot"
                              className="text-xs">
                        <Plus className="h-3 w-3 mr-1" /> Add shift
                      </Button>
                    </div>
                    <div className="space-y-3 mt-2">
                      {(cfg.slots || []).map((s, idx) => (
                        <div key={s.id || idx} className="p-3 rounded border space-y-2"
                             style={{ borderColor: "var(--border-warm)" }}
                             data-testid={`volunteer-slot-config-${idx}`}>
                          <div className="flex items-center justify-between gap-2">
                            <Input placeholder="Shift name (e.g. Morning setup)"
                                   value={s.label} onChange={(e) => updateSlot(idx, "label", e.target.value)}
                                   onBlur={() => save({ slots: cfg.slots })} />
                            <Button size="sm" variant="ghost"
                                    onClick={() => removeSlot(idx)}
                                    data-testid={`volunteer-remove-slot-${idx}`}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <Input placeholder="Starts (e.g. 8:00 AM)"
                                   value={s.starts_at} onChange={(e) => updateSlot(idx, "starts_at", e.target.value)}
                                   onBlur={() => save({ slots: cfg.slots })} />
                            <Input placeholder="Ends (e.g. 11:00 AM)"
                                   value={s.ends_at} onChange={(e) => updateSlot(idx, "ends_at", e.target.value)}
                                   onBlur={() => save({ slots: cfg.slots })} />
                          </div>
                          <div className="grid grid-cols-2 gap-2 items-center">
                            <div>
                              <Label className="text-[10px] uppercase tracking-widest">Max helpers</Label>
                              <Input type="number" min="1" max="200" value={s.max_volunteers || 1}
                                     onChange={(e) => updateSlot(idx, "max_volunteers", parseInt(e.target.value) || 1)}
                                     onBlur={() => save({ slots: cfg.slots })} />
                            </div>
                            <div className="text-xs" style={{ color: "var(--ink-soft)" }}>
                              {s.filled_count || 0} signed up
                            </div>
                          </div>
                          <Input placeholder="Description (optional)"
                                 value={s.description || ""}
                                 onChange={(e) => updateSlot(idx, "description", e.target.value)}
                                 onBlur={() => save({ slots: cfg.slots })} />
                        </div>
                      ))}
                      {(!cfg.slots || cfg.slots.length === 0) && (
                        <p className="text-xs italic" style={{ color: "var(--ink-soft)" }}>
                          No shifts yet. Add one to let helpers pick a time slot.
                        </p>
                      )}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest">Waiver title</Label>
                    <Input value={cfg.waiver_title || ""}
                           onChange={(e) => setCfg({ ...cfg, waiver_title: e.target.value })}
                           onBlur={() => save({ waiver_title: cfg.waiver_title })}
                           placeholder="Volunteer waiver & liability release" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest">Waiver body</Label>
                    <Textarea rows={6} maxLength={8000}
                              value={cfg.waiver_body || ""}
                              onChange={(e) => setCfg({ ...cfg, waiver_body: e.target.value })}
                              onBlur={() => save({ waiver_body: cfg.waiver_body })} />
                    {!cfg.waiver_body && (
                      <Button size="sm" variant="ghost" className="mt-1 text-xs"
                              onClick={() => save({ waiver_body: DEFAULT_WAIVER, waiver_title: cfg.waiver_title || "Volunteer waiver & liability release" })}>
                        Use default waiver text
                      </Button>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch checked={cfg.require_waiver_signature !== false}
                            onCheckedChange={(v) => save({ require_waiver_signature: v })} />
                    <Label className="text-xs">Require typed signature on the waiver</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch checked={!!cfg.require_phone}
                            onCheckedChange={(v) => save({ require_phone: v })} />
                    <Label className="text-xs">Require phone number</Label>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            {busy && <span className="text-xs" style={{ color: "var(--ink-soft)" }}>Saving…</span>}
          </div>

          {/* Signups roster */}
          <div className="mt-6" data-testid="volunteer-roster">
            <div className="flex items-center justify-between">
              <div className="overline" style={{ color: "var(--avt-purple)" }}>
                <Users className="h-3 w-3 inline mr-1" />
                {volunteers.length} volunteer{volunteers.length === 1 ? "" : "s"}
              </div>
            </div>
            {volunteers.length === 0 ? (
              <p className="text-xs mt-2 italic" style={{ color: "var(--ink-soft)" }}>
                No sign-ups yet. Share your link on social or with regulars to seed the roster.
              </p>
            ) : (
              <ul className="mt-3 space-y-2">
                {volunteers.map((v) => {
                  const isOpen = expandedId === v.id;
                  const slotLabels = (v.slot_ids || [])
                    .map((sid) => (cfg.slots || []).find((s) => s.id === sid)?.label)
                    .filter(Boolean);
                  return (
                    <li key={v.id}
                        data-testid={`volunteer-row-${v.id}`}
                        className="rounded-lg border"
                        style={{ borderColor: "var(--border-warm)", backgroundColor: "var(--sand)" }}>
                      <button
                        onClick={() => setExpandedId(isOpen ? null : v.id)}
                        data-testid={`volunteer-toggle-${v.id}`}
                        className="w-full text-left p-3 flex items-center gap-3"
                      >
                        {isOpen
                          ? <ChevronDown className="h-4 w-4" />
                          : <ChevronRight className="h-4 w-4" />}
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-sm">{v.name}</div>
                          <div className="text-xs" style={{ color: "var(--ink-soft)" }}>
                            {slotLabels.join(" · ") || "No shift selected"}
                          </div>
                        </div>
                        {v.host_reply && (
                          <span className="text-[10px] uppercase tracking-widest"
                                style={{ color: "var(--avt-lime)" }}>Replied</span>
                        )}
                      </button>
                      {isOpen && (
                        <div className="p-4 border-t space-y-2 text-sm"
                             style={{ borderColor: "var(--border-warm)" }}>
                          <div className="flex items-center gap-2 text-xs" style={{ color: "var(--ink-soft)" }}>
                            <Mail className="h-3 w-3" />
                            <a href={`mailto:${v.email}`} style={{ color: "var(--avt-purple)" }}>{v.email}</a>
                          </div>
                          {v.phone && (
                            <div className="flex items-center gap-2 text-xs" style={{ color: "var(--ink-soft)" }}>
                              <Phone className="h-3 w-3" />
                              <a href={`tel:${v.phone}`} style={{ color: "var(--avt-purple)" }}>{v.phone}</a>
                            </div>
                          )}
                          {v.question && (
                            <div className="p-2 rounded bg-white text-xs italic"
                                 style={{ color: "var(--ink)" }}>
                              &ldquo;{v.question}&rdquo;
                            </div>
                          )}
                          {v.waiver_signature && (
                            <div className="text-[11px]" style={{ color: "var(--ink-soft)" }}>
                              Waiver signed as &ldquo;{v.waiver_signature}&rdquo;
                              {v.waiver_signed_at && ` on ${v.waiver_signed_at.slice(0, 10)}`}
                            </div>
                          )}
                          {v.host_reply ? (
                            <div className="mt-2 p-2 rounded"
                                 style={{ backgroundColor: "rgba(107,47,186,0.06)" }}>
                              <div className="text-[10px] uppercase tracking-widest"
                                   style={{ color: "var(--avt-purple)" }}>Your reply</div>
                              <p className="text-xs mt-1" style={{ color: "var(--ink)" }}>{v.host_reply}</p>
                            </div>
                          ) : (
                            <div className="mt-2 space-y-1.5">
                              <Textarea
                                rows={2}
                                data-testid={`volunteer-reply-input-${v.id}`}
                                placeholder="Write a quick reply…"
                                value={replyDraft[v.id] || ""}
                                onChange={(e) => setReplyDraft((d) => ({ ...d, [v.id]: e.target.value }))}
                              />
                              <Button size="sm"
                                      data-testid={`volunteer-reply-send-${v.id}`}
                                      onClick={() => sendReply(v.id)}
                                      className="rounded-full">
                                <Send className="h-3 w-3 mr-1.5" /> Send reply
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}
